import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { PolicySelectors, PolicySummaryActions } from '@amfam/policy/data-access';
import { PolicyAnalytics } from '@amfam/policy/models';
// Store
import { VehicleFacade } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { PartyEmail } from '@amfam/shared/models';
import { userQuery, UserState } from '@amfam/shared/user';
import { agentQuery } from '@amfam/shared/utility/agent';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { ConfirmationModel, DockingBarService, PageTitleService } from '@amfam/ui-kit';

import {
  AnalyticsEventType,
  ReplaceVehiclePaymentsFrequency,
  ReplaceVehicleQuickQuoteOperation
} from '../+state/quick-quote-feature/quick-quote-enum';
import { QuickQuoteFeatureFacade } from '../+state/quick-quote-feature/quick-quote-feature.facade';
import { quickQuoteSelectors } from '../+state/quick-quote-feature/quick-quote-feature.selectors';
import { QuickQuoteFeatureService } from '../services/quick-quote-feature.service';

@Component({
  selector: 'ds-replace-vehicle-confirmation',
  templateUrl: './replace-vehicle-confirmation.component.html',
  styleUrls: ['./replace-vehicle-confirmation.component.scss'],
  standalone: false
})
export class ReplaceVehicleConfirmationComponent implements OnInit, AfterViewInit {
  public myAccountEmail: string;
  public myaccountPrimaryEmail: PartyEmail;
  public user: UserState;
  private agentName: string;
  private vehicleId: string;
  public confirmationData: ConfirmationModel;

  public isEligibleAdvancedVehicle = false;
  public sendToAgent: boolean;
  public agents;

  context$ = this.quickQuoteFeatureFacade.context$;

  paymentFrequency: ReplaceVehiclePaymentsFrequency;

  constructor(
    private route: ActivatedRoute,
    private pageTitle: PageTitleService,
    private copyService: CopyService,
    private dockingBarService: DockingBarService,
    private store: Store<any>,
    public roleService: ImpersonateRolesService,
    private analyticsFacade: AnalyticsFacade,
    private quickQuoteFeatureService: QuickQuoteFeatureService,
    private quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    private vehicleFacade: VehicleFacade,
    private router: Router
  ) {
    this.paymentFrequency =
      this.router.getCurrentNavigation()?.extras?.state?.paymentFrequency ?? '';
  }

  ngOnInit() {
    this.pageTitle.set('Replace a Vehicle');
    this.dockingBarService.registerHeading('Replace a Vehicle');

    combineLatest(
      this.route.params,
      this.store.select(userQuery.selectUserState),
      this.store.select(PolicySelectors.selectAdvanceAutoPolicyRisks),
      this.quickQuoteFeatureFacade.operation$,
      this.quickQuoteFeatureFacade.quickQuote$,
      (params, userState, risks, confirmationType, vehiclePremium) => ({
        params,
        userState,
        risks,
        confirmationType,
        vehiclePremium
      })
    )
      .pipe(
        filter(state => !!state.params && !!state.userState && !!state.risks),
        take(1)
      )
      .subscribe(res => {
        this.myAccountEmail = this.getMyAccountEmailAddress(res.userState);
        if (res.risks) {
          this.vehicleId = res.params['id'];
          const matchingVehicle = res.risks.find(vehicle => {
            return _get(vehicle, 'id') === this.vehicleId;
          });
          this.agentName = _get(matchingVehicle, 'policy.assignedProducer.name');
          this.isEligibleAdvancedVehicle =
            this.quickQuoteFeatureService.getAdvancePolicyEligibility(matchingVehicle);
        }
        if (res.confirmationType === ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_BIND) {
          this.buildConfirmationDataForOnlineBindEnabled(
            'policy.replaceVehicleConfirmation.advanceVehicleOnlineBindSuccessHeading',
            'policy.replaceVehicleConfirmation.advanceVehicleOnlineBindNextSteps',
            'policy.replaceVehicleConfirmation.nextStepsHeading'
          );
          this.quickQuoteFeatureFacade.sendAnalytics(
            AnalyticsEventType.REPLACE_VEHICLE_BIND_COMPLETE,
            this.agentName,
            _get(res, 'vehiclePremium.workOrderNumber', ''),
            this.paymentFrequency
          );
          this.sendToAgent = false;
        } else if (
          [
            ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_SEND_TO_AGENT_BIND,
            ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_UW_SEND_TO_AGENT,
            ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_TPI_SEND_TO_AGENT,
            ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_COMPARE_QUOTE_SEND_TO_AGENT,
            ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_FUTURE_PAYMENTS_SEND_TO_AGENT,
            ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_HARDSTOP_OTHER_SEND_TO_AGENT
          ].includes(res.confirmationType)
        ) {
          this.sendToAgent = true;
          let analyticsType = AnalyticsEventType.REPLACE_VEHICLE_QUOTE_OR_BIND_SEND_TO_AGENT;

          if (
            res.confirmationType ===
            ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_UW_SEND_TO_AGENT
          ) {
            analyticsType = AnalyticsEventType.REPLACE_VEHICLE_UW_CANCEL_SEND_TO_AGENT;
          } else if (
            res.confirmationType ===
            ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_TPI_SEND_TO_AGENT
          ) {
            analyticsType = AnalyticsEventType.REPLACE_VEHICLE_TPI_CANCEL_SEND_TO_AGENT;
          } else if (
            res.confirmationType ===
            ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_HARDSTOP_OTHER_SEND_TO_AGENT
          ) {
            analyticsType =
              AnalyticsEventType.REPLACE_VEHICLE_HARD_STOP_OTHER_SEND_TO_AGENT_SUCCESS;
          }

          this.quickQuoteFeatureFacade.sendAnalytics(analyticsType);
        } else if (
          res.confirmationType === ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_SEND_AGENT ||
          res.confirmationType ===
            ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_ERROR_SEND_AGENT
        ) {
          this.sendToAgent = true;
          this.quickQuoteFeatureFacade.sendAnalytics(
            AnalyticsEventType.REPLACE_VEHICLE_SEND_TO_AGENT_CONFIRMATION,
            this.agentName,
            _get(res, 'vehiclePremium.workOrderNumber', '')
          );
        } else {
          this.sendToAgent = true;
        }
      });

    combineLatest(
      this.store.select(quickQuoteSelectors.getAllActiveAgents),
      this.store.select(agentQuery.getAreAllAgentsLoaded),
      (agents, loaded) => ({ agents, loaded })
    )
      .pipe(
        filter(data => !!data.loaded),
        map(data => data.agents),
        take(1)
      )
      .subscribe(data => (this.agents = data));
  }

  ngAfterViewInit() {
    if (!this.isEligibleAdvancedVehicle) {
      this.analyticsFacade.trackPage(
        PolicyAnalytics.pagePolicyAddVehicleGraceNotificationSentToAgent
      );
    }
  }

  buildConfirmationData() {
    let heading, nextSteps;
    if (this.isEligibleAdvancedVehicle) {
      heading = this.copyService.getCopy('policy.replaceVehicleConfirmation.advanceVehicleHeading');
      nextSteps = this.copyService.getCopy(
        'policy.replaceVehicleConfirmation.advanceVehicleNextSteps',
        {
          myAccountEmail: this.myAccountEmail
        }
      );
    } else {
      heading = this.copyService.getCopy(
        'policy.replaceVehicleConfirmation.nonAdvanceVehicleHeading'
      );
      nextSteps = this.copyService.getCopy(
        'policy.replaceVehicleConfirmation.nonAdvanceVehicleNextSteps'
      );
    }
    this.confirmationData = {
      confirmation: {
        heading: heading
      },
      nextSteps: {
        heading: "You're all set!",
        text: nextSteps
      }
    };
  }

  buildConfirmationDataForOnlineBindEnabled(
    headdingCopyPath: string,
    nextStepsCopyPath: string,
    nextStepsHeading?: string
  ) {
    let heading, nextSteps;
    if (this.isEligibleAdvancedVehicle) {
      heading = this.copyService.getCopy(headdingCopyPath);
      nextSteps = this.copyService.getCopy(nextStepsCopyPath, {
        myAccountEmail: this.myAccountEmail
      });
    } else {
      heading = this.copyService.getCopy(
        'policy.replaceVehicleConfirmation.nonAdvanceVehicleHeading'
      );
      nextSteps = this.copyService.getCopy(
        'policy.replaceVehicleConfirmation.nonAdvanceVehicleNextSteps'
      );
    }
    this.confirmationData = {
      confirmation: {
        heading: heading
      },
      nextSteps: {
        heading: nextStepsHeading ? this.copyService.getCopy(nextStepsHeading) : '',
        text: nextSteps
      }
    };
  }

  done(): void {
    /**
     * AS: If we have replaced a vehicle , we need to refresh the policies to get the latest policies which was bound.
     */
    this.store.dispatch(PolicySummaryActions.LoadSummaries());
    this.vehicleFacade.vehiclePolicyChangeEntryPoint$.pipe(take(1)).subscribe(path => {
      this.store.dispatch(
        fromRouterActions.Go({
          path: [path || '/overview']
        })
      );
    });
  }

  getMyAccountEmailAddress(user) {
    if (user) {
      const emailAddress = _get(user, 'emailAddress', 'your My Account email address');
      return 'at <b>' + emailAddress + '</b>'; // Just making it so that the sentence makes sense if the email address doesn't render.
    } else {
      return 'at your My Account email address'; // In case they end up here while logged out. Remove this if a route guard prevents this
    }
  }
}
